import { FC, HTMLAttributes } from "react";
import { useStyles } from "./LevelDetail.style";
import GZIcon from "../../assets/rankGem/webp/gz_coin_1x.webp";
import GZIcon2x from "../../assets/rankGem/webp/icon2x/gz_coin_2x.webp";
import GZIcon3x from "../../assets/rankGem/webp/icon3x/gz_coin_3x.webp";
import { User } from "../../models/user";
import { useQuery } from "@apollo/client";
import { getConfigGQL, meGQL, Query } from "@onmo/graphql/graphql";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyVariant } from "../../constants/typography";
import { StaticImage } from "../../components/common/StaticImage";
import { ProgressBar } from "../../components/common/ProgressBar";
import Badge from "../../components/core/Badge/Badge";
import AppBar from "../../components/common/AppBar/AppBar";
import { Icon } from "../../components/core/Icon/Icon";
import { HistoryGaming } from "../../models/historyGaming";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const LevelDetail: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: dataMe } = useQuery<Query>(meGQL);
  const { data: dataConfig } = useQuery(getConfigGQL);

  const userRank = User.getCurrentRank(
    dataMe?.me.coinWallets,
    dataConfig?.config.tierLevels,
  );
  const levelStage = User.formatLevelStage(dataConfig?.config.tierLevels);
  const gzTokens = userRank?.xp || 0;

  const renderGem = (size: number, value?: number) => {
    if (value === undefined)
      return (
        <StaticImage
          height={size}
          width={size}
          alt="gem"
          src={GZIcon}
          src2x={GZIcon2x}
          src3x={GZIcon3x}
          className={classes.gemIconTo}
        />
      );

    return (
      <Typography
        variant={TypographyVariant.BodyB6Bold}
        className={classes.gem}
      >
        <StaticImage
          height={size}
          width={size}
          alt="gem"
          src={GZIcon}
          src2x={GZIcon2x}
          src3x={GZIcon3x}
          className={classes.gemIconTo}
        />
        {value}
      </Typography>
    );
  };
  return (
    <div className={classes.root}>
      <AppBar
        leftItems={
          <Icon
            name="icon-bold-arrow-left-3"
            onClick={() => HistoryGaming.back(navigate)}
            data-testid="back-icon"
          />
        }
        centerItems={
          <Typography
            variant={TypographyVariant.BodyB4Medium}
            data-testid="level-detail-title"
          >
            {t("profile_Levels")}
          </Typography>
        }
      />
      <div className={classes.content}>
        <div className={classes.levelContainer}>
          {userRank?.id && (
            <Badge
              size={"xxxxxl"}
              type={userRank?.id}
              id={"badge"}
              data-testid="current-rank-badge"
            />
          )}
          <Typography
            variant={TypographyVariant.BodyB4Regular}
            data-testid="your-level-title"
          >
            {t("profile_Your Level")}
          </Typography>
          <Typography
            variant={TypographyVariant.HeadingH4Bold}
            className={classes.levelName}
            data-testid="user-rank-name"
          >
            {userRank?.name}
          </Typography>
          <div className={classes.progressContainer}>
            <ProgressBar
              color={userRank?.color || ""}
              progress={userRank?.levelPercent}
              size="medium"
              data-testid="gem-progress-bar"
            />
            {userRank?.nextLevel && (
              <Badge
                size={"xxl"}
                type={userRank?.nextLevel?.id}
                id={"badge"}
                data-testid="rank-badge"
              />
            )}
          </div>
          {userRank?.nextLevel && (
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              className={classes.gemNeeded}
            >
              <b>{userRank?.nextLevel.xp - gzTokens}</b>
              <StaticImage
                src={GZIcon}
                src2x={GZIcon2x}
                src3x={GZIcon3x}
                width={15}
              />
              to {userRank.nextLevel.name}
            </Typography>
          )}
        </div>
        <div className={classes.allLevelsContainer}>
          <div className={classes.gemCollected}>
            <StaticImage
              src={GZIcon}
              src2x={GZIcon2x}
              src3x={GZIcon3x}
              width={36}
              height={36}
            />
            <div className={classes.gemCollectedContent}>
              <Typography
                variant={TypographyVariant.BodyB6Regular}
                data-testid="total-gems-title"
              >
                {t("common_Total")}
              </Typography>
              <Typography variant={TypographyVariant.BodyB3Bold}>
                {t("profile_Collected")}
              </Typography>
            </div>
            <Typography variant={TypographyVariant.BodyB2Bold}>
              {gzTokens}
            </Typography>
          </div>
          <div className={classes.allLevelsContent}>
            <Typography
              variant={TypographyVariant.HeadingH6Bold}
              data-testid="all-levels-title"
            >
              {t("profile_All levels")}
            </Typography>
            {levelStage.map((level) => {
              if (!level.xp) return null;
              return (
                <div className={classes.levelItem}>
                  <Badge
                    size={"xxl-48"}
                    type={level.id}
                    data-testid="rank-badge"
                  />
                  <Typography
                    variant={TypographyVariant.BodyB6Bold}
                    className={classes.gemCollectedContent}
                    data-testid={level?.name?.toLowerCase().replace(/ /g, "-")}
                  >
                    {level?.name}
                  </Typography>
                  {renderGem(20, level.xp || 0)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
